/**
 * Producto — módulo nuevo (feedback Compras/Ventas).
 *
 * Permite parametrizar:
 *   · Variedades: catálogo (FIXED_VARIETIES + custom). Las custom se
 *     persisten en data.customVarieties[] y aparecen automáticamente en
 *     todos los dropdowns (ventas, compras, devoluciones, inventario).
 *   · Combos: catálogo de combos preestablecidos. PREDEFINED_COMBOS es
 *     read-only (definido en el paquete shared) y los combos custom se
 *     gestionan acá con CRUD completo. La pantalla de Nueva Venta los
 *     consume desde GET /api/combos (unión).
 */
import { useMemo, useState } from 'react'
import {
  Leaf,
  Package,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { BOX_SIZES } from '@expogreens/shared'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardContent } from '@/components/ui/Card'
import { Modal, Field, Input, Select } from '@/components/ui/Modal'
import { useApi } from '@/lib/useApi'
import { useMutation } from '@/lib/useMutation'
import { apiCall } from '@/lib/api'

const EMPTY_COMBO_ITEM = { variety: '', quantity: 0, stemLength: 60 }
const EMPTY_BOX = () => ({ boxSize: 'fullbox', boxCount: 1, items: [] })

export default function ProductsPage() {
  const varieties = useApi('/varieties')
  const combos = useApi('/combos')

  const [newVariety, setNewVariety] = useState('')
  const [editingCombo, setEditingCombo] = useState(null) // null | {} | combo
  const [comboName, setComboName] = useState('')
  const [comboDesc, setComboDesc] = useState('')
  const [comboItems, setComboItems] = useState([{ ...EMPTY_COMBO_ITEM }])
  // Empaque por defecto del combo — LISTA de cajas. Cada caja define
  // su tipo, cantidad y los items (variedad/longitud/cantidad) que van
  // dentro. Al usar el combo en una venta, esta lista se aplica en el
  // Step 3 multiplicando boxCount × multiplier del combo.
  const [comboPacking, setComboPacking] = useState([])

  const addVariety = useMutation((name) =>
    apiCall('/varieties', { method: 'POST', body: { name } }),
  )
  const delVariety = useMutation((name) =>
    apiCall(`/varieties/${encodeURIComponent(name)}`, { method: 'DELETE' }),
  )
  const createCombo = useMutation((payload) =>
    apiCall('/combos', { method: 'POST', body: payload }),
  )
  const updateCombo = useMutation(({ id, payload }) =>
    apiCall(`/combos/${id}`, { method: 'PUT', body: payload }),
  )
  const deleteCombo = useMutation((id) =>
    apiCall(`/combos/${id}`, { method: 'DELETE' }),
  )

  // Combos preestablecidos vs custom. La heurística: los que vienen de
  // PREDEFINED_COMBOS tienen id con prefijo "COMBO-" (sin C) y NO tienen
  // isCustom; los custom traen isCustom=true.
  const { fixedCombos, customCombos } = useMemo(() => {
    const list = combos.data || []
    return {
      fixedCombos: list.filter((c) => !c.isCustom),
      customCombos: list.filter((c) => c.isCustom),
    }
  }, [combos.data])

  const openNewCombo = () => {
    setEditingCombo({})
    setComboName('')
    setComboDesc('')
    setComboItems([{ ...EMPTY_COMBO_ITEM }])
    setComboPacking([])
  }
  const openEditCombo = (c) => {
    setEditingCombo(c)
    setComboName(c.name || '')
    setComboDesc(c.description || '')
    setComboItems(
      (c.items || []).map((it) => ({
        variety: it.variety || '',
        quantity: Number(it.quantity) || 0,
        stemLength: Number(it.stemLength) || 60,
      })),
    )
    // Hidratar el empaque por defecto soportando dos formatos:
    //   - array de cajas (formato actual)
    //   - objeto único {boxSize, boxCount} (legacy) → 1 caja con TODOS
    //     los items del combo.
    const dp = c.defaultPacking
    if (Array.isArray(dp)) {
      setComboPacking(
        dp.map((b) => ({
          boxSize: b.boxSize || 'fullbox',
          boxCount: Number(b.boxCount) || 1,
          items: (b.items || []).map((it) => ({
            variety: it.variety || '',
            stemLength: Number(it.stemLength) || 0,
            quantity: Number(it.quantity) || 0,
          })),
        })),
      )
    } else if (dp && typeof dp === 'object') {
      setComboPacking([
        {
          boxSize: dp.boxSize || 'fullbox',
          boxCount: Number(dp.boxCount) || 1,
          items: (c.items || []).map((it) => ({
            variety: it.variety || '',
            stemLength: Number(it.stemLength) || 0,
            quantity: Number(it.quantity) || 0,
          })),
        },
      ])
    } else {
      setComboPacking([])
    }
  }
  const closeComboModal = () => {
    setEditingCombo(null)
    setComboName('')
    setComboDesc('')
    setComboItems([{ ...EMPTY_COMBO_ITEM }])
    setComboPacking([])
  }

  // ── Handlers del editor de cajas por defecto ──────────────────────
  const addComboBox = () => setComboPacking((l) => [...l, EMPTY_BOX()])
  const removeComboBox = (i) =>
    setComboPacking((l) => l.filter((_, idx) => idx !== i))
  const patchComboBox = (i, patch) =>
    setComboPacking((l) => l.map((b, idx) => (idx === i ? { ...b, ...patch } : b)))
  const addBoxItem = (boxIdx) =>
    setComboPacking((l) =>
      l.map((b, i) =>
        i === boxIdx
          ? { ...b, items: [...(b.items || []), { variety: '', stemLength: 60, quantity: 0 }] }
          : b,
      ),
    )
  const removeBoxItem = (boxIdx, itemIdx) =>
    setComboPacking((l) =>
      l.map((b, i) =>
        i === boxIdx
          ? { ...b, items: (b.items || []).filter((_, j) => j !== itemIdx) }
          : b,
      ),
    )
  const patchBoxItem = (boxIdx, itemIdx, patch) =>
    setComboPacking((l) =>
      l.map((b, i) =>
        i === boxIdx
          ? {
              ...b,
              items: (b.items || []).map((it, j) =>
                j === itemIdx ? { ...it, ...patch } : it,
              ),
            }
          : b,
      ),
    )

  // Prellena las cajas con los items totales del combo (1 sola caja con
  // todo adentro). Atajo cuando empaque y contenido coinciden.
  const presetFromComboItems = () => {
    setComboPacking([
      {
        boxSize: 'fullbox',
        boxCount: 1,
        items: comboItems
          .filter((it) => it.variety && Number(it.quantity) > 0)
          .map((it) => ({
            variety: it.variety,
            stemLength: Number(it.stemLength) || 0,
            quantity: Number(it.quantity) || 0,
          })),
      },
    ])
  }

  const onAddVariety = async (e) => {
    e.preventDefault()
    if (!newVariety.trim()) return
    try {
      await addVariety.mutate(newVariety.trim())
      toast.success(`Variedad ${newVariety} creada`)
      setNewVariety('')
      varieties.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onDelVariety = async (name) => {
    if (!confirm(`¿Eliminar la variedad ${name}? (solo afecta a variedades creadas en este módulo)`)) return
    try {
      await delVariety.mutate(name)
      toast.success(`Variedad ${name} eliminada`)
      varieties.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onSubmitCombo = async (e) => {
    e.preventDefault()
    if (!comboName.trim()) return toast.error('Nombre del combo requerido')
    const cleaned = comboItems
      .filter((it) => it.variety && Number(it.quantity) > 0)
      .map((it) => ({
        variety: it.variety,
        quantity: Number(it.quantity),
        stemLength: Number(it.stemLength) || 60,
      }))
    if (cleaned.length === 0) return toast.error('Agrega al menos un item al combo')
    // defaultPacking: array de cajas, cada una con sus items. El
    // backend lo normaliza (filtra cajas vacías, valida boxSize, etc.).
    const cleanedPacking = comboPacking
      .map((b) => ({
        boxSize: b.boxSize || 'fullbox',
        boxCount: Math.max(1, Number(b.boxCount) || 1),
        items: (b.items || [])
          .filter((it) => it.variety && Number(it.quantity) > 0)
          .map((it) => ({
            variety: it.variety,
            stemLength: Number(it.stemLength) || 0,
            quantity: Number(it.quantity) || 0,
          })),
      }))
      .filter((b) => b.items.length > 0)
    const payload = {
      name: comboName,
      description: comboDesc,
      items: cleaned,
      defaultPacking: cleanedPacking,
    }
    try {
      if (editingCombo?.id) {
        await updateCombo.mutate({ id: editingCombo.id, payload })
        toast.success(`Combo ${editingCombo.id} actualizado`)
      } else {
        await createCombo.mutate(payload)
        toast.success('Combo creado')
      }
      closeComboModal()
      combos.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onDelCombo = async (c) => {
    if (!confirm(`¿Eliminar combo ${c.name}?`)) return
    try {
      await deleteCombo.mutate(c.id)
      toast.success('Combo eliminado')
      combos.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const patchComboItem = (i, patch) =>
    setComboItems((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))
  const addComboItem = () => setComboItems((l) => [...l, { ...EMPTY_COMBO_ITEM }])
  const removeComboItem = (i) =>
    setComboItems((l) => (l.length > 1 ? l.filter((_, idx) => idx !== i) : l))

  return (
    <div className="space-y-6">
      <PageHeader
        title="Producto"
        subtitle="Catálogo de variedades y combos parametrizables"
        icon={Leaf}
      />

      {/* ── Variedades ─────────────────────────────────────────────── */}
      <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-expo-dark">Variedades</h3>
            <form onSubmit={onAddVariety} className="flex gap-2">
              <Input
                value={newVariety}
                onChange={(e) => setNewVariety(e.target.value)}
                placeholder="Nueva variedad…"
                className="w-56"
              />
              <Button type="submit" disabled={addVariety.loading || !newVariety.trim()}>
                <Plus size={16} />
                <span>Añadir</span>
              </Button>
            </form>
          </div>
          {varieties.data && (
            <div className="flex flex-wrap gap-2">
              {varieties.data.map((v) => (
                <span
                  key={v}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-expo-light/60 text-expo-dark text-sm"
                >
                  <Leaf size={14} className="text-expo-primary" />
                  {v}
                  {/* Solo damos botón eliminar a las custom. Las del
                      catálogo fijo no se pueden borrar — son shared. */}
                  <button
                    type="button"
                    onClick={() => onDelVariety(v)}
                    className="text-gray-400 hover:text-red-600"
                    title="Eliminar (solo si es custom)"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Combos ─────────────────────────────────────────────────── */}
      <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-expo-dark">Combos</h3>
            <Button onClick={openNewCombo}>
              <Plus size={16} />
              <span>Nuevo combo</span>
            </Button>
          </div>

          {/* Preestablecidos (catálogo shared, read-only) */}
          {fixedCombos.length > 0 && (
            <>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Preestablecidos
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {fixedCombos.map((c) => (
                  <div
                    key={c.id}
                    className="rounded-xl border border-gray-200 bg-white p-3"
                  >
                    <div className="font-semibold text-expo-dark">{c.name}</div>
                    <div className="text-xs text-gray-500 mb-2">
                      {c.description || ''}
                    </div>
                    <ul className="text-sm text-expo-dark space-y-0.5">
                      {(c.items || []).map((it, i) => (
                        <li key={i}>
                          {it.quantity} {it.variety} {it.stemLength}cm
                        </li>
                      ))}
                    </ul>
                    {Array.isArray(c.defaultPacking) && c.defaultPacking.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {c.defaultPacking.map((b, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 text-[11px] text-expo-primary bg-expo-primary/10 rounded-full px-2 py-0.5"
                            title={(b.items || [])
                              .map(
                                (it) =>
                                  `${it.quantity} ${it.variety} ${it.stemLength}cm`,
                              )
                              .join(' · ')}
                          >
                            <Package size={12} />
                            {BOX_SIZES[b.boxSize] || b.boxSize} × {b.boxCount}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Custom (CRUD) */}
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Combos personalizados
          </div>
          {customCombos.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              Sin combos personalizados. Crea uno con "Nuevo combo".
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {customCombos.map((c) => (
                <div
                  key={c.id}
                  className="rounded-xl border border-expo-primary/40 bg-expo-light/30 p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-expo-dark">{c.name}</div>
                      <div className="text-xs text-gray-500">
                        {c.description || ''}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => openEditCombo(c)}
                        className="p-1 text-gray-500 hover:text-expo-primary"
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelCombo(c)}
                        className="p-1 text-gray-500 hover:text-red-600"
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <ul className="mt-2 text-sm text-expo-dark space-y-0.5">
                    {(c.items || []).map((it, i) => (
                      <li key={i}>
                        {it.quantity} {it.variety} {it.stemLength}cm
                      </li>
                    ))}
                  </ul>
                  {Array.isArray(c.defaultPacking) && c.defaultPacking.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {c.defaultPacking.map((b, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 text-[11px] text-expo-primary bg-white rounded-full px-2 py-0.5 border border-expo-primary/30"
                          title={(b.items || [])
                            .map(
                              (it) =>
                                `${it.quantity} ${it.variety} ${it.stemLength}cm`,
                            )
                            .join(' · ')}
                        >
                          <Package size={12} />
                          {BOX_SIZES[b.boxSize] || b.boxSize} × {b.boxCount}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        open={Boolean(editingCombo)}
        onClose={closeComboModal}
        title={editingCombo?.id ? `Editar combo ${editingCombo.id}` : 'Nuevo combo'}
        size="lg"
      >
        <form onSubmit={onSubmitCombo} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre" required>
              <Input
                value={comboName}
                onChange={(e) => setComboName(e.target.value)}
                required
              />
            </Field>
            <Field label="Descripción">
              <Input
                value={comboDesc}
                onChange={(e) => setComboDesc(e.target.value)}
              />
            </Field>
          </div>

          {/*
            Empaque por defecto del combo — LISTA de cajas. Cada caja
            tiene su tipo, cantidad y los items (variedad/longitud/
            cantidad) que van adentro. Un mismo combo puede empacarse
            en varias cajas (ej. una Full Box con verdes + una Half
            Box con tropicales). Editable en la venta antes de
            despachar.
          */}
          <div className="rounded-xl border border-expo-primary/30 bg-expo-light/30 p-3 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Package size={16} className="text-expo-dark" />
              <span className="text-sm font-semibold text-expo-dark">
                Cajas por defecto
              </span>
              <span className="text-xs font-normal text-gray-500">
                (se aplican al usar el combo en una venta, editable después)
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={presetFromComboItems}
                  className="text-xs text-expo-primary hover:text-expo-dark font-medium"
                  title="Atajo: 1 caja con todos los items del combo"
                >
                  Auto: todo en una caja
                </button>
                <button
                  type="button"
                  onClick={addComboBox}
                  className="text-xs text-expo-primary hover:text-expo-dark font-medium flex items-center gap-1"
                >
                  <Plus size={14} />
                  Añadir caja
                </button>
              </div>
            </div>

            {comboPacking.length === 0 ? (
              <p className="text-xs text-gray-500 italic">
                Sin cajas configuradas. Sin empaque por defecto, el operario
                tendrá que armar las cajas a mano en cada venta.
              </p>
            ) : (
              <div className="space-y-3">
                {comboPacking.map((box, bi) => (
                  <div
                    key={bi}
                    className="rounded-xl border border-gray-200 bg-white p-3 space-y-2"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Caja #{bi + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeComboBox(bi)}
                        className="ml-auto p-1 text-gray-400 hover:text-red-600"
                        title="Eliminar esta caja"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-7">
                        <Field label="Tipo de caja">
                          <Select
                            value={box.boxSize}
                            onChange={(e) =>
                              patchComboBox(bi, { boxSize: e.target.value })
                            }
                          >
                            {Object.entries(BOX_SIZES).map(([id, label]) => (
                              <option key={id} value={id}>
                                {label}
                              </option>
                            ))}
                          </Select>
                        </Field>
                      </div>
                      <div className="col-span-5">
                        <Field
                          label="Cajas iguales"
                          hint="Por unidad del combo (el multiplicador de la venta vuelve a multiplicar)."
                        >
                          <Input
                            type="number"
                            min="1"
                            value={box.boxCount}
                            onChange={(e) =>
                              patchComboBox(bi, { boxCount: e.target.value })
                            }
                          />
                        </Field>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-expo-dark">
                          Contenido de cada caja
                        </span>
                        <button
                          type="button"
                          onClick={() => addBoxItem(bi)}
                          className="text-xs text-expo-primary hover:text-expo-dark font-medium flex items-center gap-1"
                        >
                          <Plus size={12} />
                          Añadir variedad
                        </button>
                      </div>
                      <div className="space-y-1">
                        <div className="grid grid-cols-12 gap-2 px-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                          <div className="col-span-6">Variedad</div>
                          <div className="col-span-2">cm</div>
                          <div className="col-span-3">Cantidad</div>
                          <div className="col-span-1" />
                        </div>
                        {(box.items || []).length === 0 && (
                          <p className="text-xs text-gray-400 italic px-2">
                            Caja vacía — agrega al menos una variedad.
                          </p>
                        )}
                        {(box.items || []).map((it, ii) => (
                          <div
                            key={ii}
                            className="grid grid-cols-12 gap-2 bg-expo-light/40 p-2 rounded-lg items-center"
                          >
                            <div className="col-span-6">
                              <Select
                                value={it.variety}
                                onChange={(e) =>
                                  patchBoxItem(bi, ii, { variety: e.target.value })
                                }
                              >
                                <option value="">Variedad…</option>
                                {(varieties.data || []).map((v) => (
                                  <option key={v} value={v}>
                                    {v}
                                  </option>
                                ))}
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="number"
                                min="0"
                                value={it.stemLength}
                                onChange={(e) =>
                                  patchBoxItem(bi, ii, { stemLength: e.target.value })
                                }
                              />
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="number"
                                min="0"
                                value={it.quantity}
                                onChange={(e) =>
                                  patchBoxItem(bi, ii, { quantity: e.target.value })
                                }
                              />
                            </div>
                            <div className="col-span-1 flex justify-end">
                              <button
                                type="button"
                                onClick={() => removeBoxItem(bi, ii)}
                                className="p-1 text-gray-400 hover:text-red-600"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-expo-dark">Items</span>
              <button
                type="button"
                onClick={addComboItem}
                className="text-sm text-expo-primary hover:text-expo-dark font-medium flex items-center gap-1"
              >
                <Plus size={16} />
                Añadir item
              </button>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <div className="col-span-5">Variedad</div>
                <div className="col-span-3">Cantidad</div>
                <div className="col-span-3">Longitud (cm)</div>
                <div className="col-span-1" />
              </div>
              {comboItems.map((it, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 bg-expo-light/50 p-3 rounded-xl items-center"
                >
                  <div className="col-span-5">
                    <Select
                      value={it.variety}
                      onChange={(e) => patchComboItem(idx, { variety: e.target.value })}
                      required
                    >
                      <option value="">Variedad…</option>
                      {(varieties.data || []).map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      min="0"
                      value={it.quantity}
                      onChange={(e) => patchComboItem(idx, { quantity: e.target.value })}
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      min="0"
                      value={it.stemLength}
                      onChange={(e) => patchComboItem(idx, { stemLength: e.target.value })}
                    />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeComboItem(idx)}
                      disabled={comboItems.length === 1}
                      className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-30"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={closeComboModal}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createCombo.loading || updateCombo.loading}
            >
              {(createCombo.loading || updateCombo.loading)
                ? 'Guardando…'
                : editingCombo?.id
                  ? 'Guardar cambios'
                  : 'Crear combo'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
